"use client";

import * as React from "react";
import { format } from "date-fns";
import { formatUSDate } from "../../../util/common-functions";
import { CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";

import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Calendar } from "../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { PopoverContentProps } from "@radix-ui/react-popover";

type PickerType = "single" | "range";

interface DatePickerProps {
  type: PickerType;
  value: Date | DateRange | undefined;
  onChange: (value: Date | DateRange | undefined) => void;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  compact?: boolean;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  usePortal?: boolean;
  popoverProps?: Partial<PopoverContentProps>;
  classNames?: {
    root?: string;
    caption?: string;
    nav?: string;
    nav_button?: string;
    navIcon?: string;
    table?: string;
    headRow?: string;
    head_cell?: string;
    bodyRow?: string;
    bodyCell?: string;
    day?: string;
    selected?: string;
    caption_label?: string;
    cell?: string;
  };
}

export const DatePicker: React.FC<DatePickerProps> = ({
  type,
  value,
  onChange,
  disabled = false,
  minDate,
  maxDate,
  compact = false,
  className = "",
  open,
  onOpenChange,
  usePortal,
  popoverProps,
  classNames,
}) => {
  const displayValue = () => {
    if (type === "single" && value instanceof Date) {
      return formatUSDate(value);
    } else if (
      type === "range" &&
      value &&
      (value as DateRange).from &&
      (value as DateRange).to
    ) {
      const { from, to } = value as DateRange;
      return `${formatUSDate(from!)} - ${formatUSDate(to!)}`;
    }
    return "";
  };

  const [internalRange, setInternalRange] = React.useState<
    DateRange | undefined
  >(type === "range" ? (value as DateRange) : undefined);

  React.useEffect(() => {
    if (type === "range") {
      setInternalRange(value as DateRange);
    }
  }, [value, type]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full flex items-center justify-between font-normal rounded-[4px]",
            compact ? "h-10 text-sm px-2 py-1" : "h-11 text-base px-3 py-2",
            type === "single"
              ? "border border-gray-300 hover:border-brand-secondary focus:ring-1 focus:ring-brand-secondary"
              : "border-none",
            !displayValue() && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <span className={displayValue() ? "text-black" : "text-gray-600"}>
            {displayValue() || "Select Date"}
          </span>
          <CalendarIcon
            className={cn(
              "ml-2 text-gray-600",
              type === "single" ? "h-[12px] w-[12px]" : "h-[10px] w-[10px]"
            )}
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-auto p-4 bg-white shadow-md rounded-md border z-[999] max-h-[300px]"
        {...(!usePortal ? { container: undefined } : {})}
        {...popoverProps}
      >
        {type === "single" ? (
          <Calendar
            mode="single"
            selected={value as Date}
            onSelect={(val) => onChange(val)}
            fromDate={minDate}
            toDate={maxDate}
            disabled={disabled}
            numberOfMonths={1}
            initialFocus
            classNames={classNames}
          />
        ) : (
          <Calendar
            mode="range"
            selected={
              value && (value as DateRange).from
                ? (value as DateRange)
                : undefined
            }
            onSelect={(val) => {
              if (!val) {
                setInternalRange(undefined);
                onChange(undefined);
                return;
              }

              if (
                internalRange?.from &&
                internalRange?.to &&
                val?.from &&
                !val?.to
              ) {
                setInternalRange({ from: val.from, to: undefined });
                onChange({ from: val.from, to: undefined });
                return;
              }

              // Normal update
              setInternalRange(val);
              onChange(val);
            }}
            fromDate={minDate}
            toDate={maxDate}
            disabled={disabled}
            numberOfMonths={1}
            initialFocus
          />
        )}
      </PopoverContent>
    </Popover>
  );
};
